import { useMemo, useRef } from 'react';
import { useFormik } from 'formik';

import { Button, FileField, Modal, SingleSelectField, Text, TextAreaField, TextField } from '../../common/components';
import { t } from '../../common/i18n';
import Yup from '../../common/utils/FormValidation';
import { Issue, IssueCategory, IssueFormValues } from '../types';

const namespace = 'Issues';

interface IssueFormModalProps {
  isShown: boolean;
  /** Při úpravě existující hlášení, jinak null (vytvoření). */
  selected: Issue | null;
  /** Kategorie budovy (z API) – nabízí se jen aktivní. */
  categories: IssueCategory[];
  onHide: () => void;
  onSubmit: (values: IssueFormValues, id: string | null) => Promise<void>;
}

const validationSchema = Yup.object({
  title: Yup.string().required(),
  description: Yup.string().required(),
  categoryId: Yup.string().required(),
});

export const IssueFormModal: React.FC<IssueFormModalProps> = ({ isShown, selected, categories, onHide, onSubmit }) => {
  const isEdit = selected !== null;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formik = useFormik<IssueFormValues>({
    enableReinitialize: true,
    initialValues: {
      title: selected?.title ?? '',
      description: selected?.description ?? '',
      categoryId: selected?.category.id ?? '',
      location: selected?.location ?? '',
      photos: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await onSubmit(values, selected?.id ?? null);
        formik.resetForm();
        if (fileInputRef.current) fileInputRef.current.value = '';
      } finally {
        formik.setSubmitting(false);
      }
    },
  });

  // Aktivní kategorie; u editace navíc ta aktuální, i kdyby byla mezitím deaktivovaná.
  const categoryOptions = useMemo(
    () =>
      categories
        .filter((category) => category.active || category.id === selected?.category.id)
        .map((category) => ({ value: category.id, label: category.name })),
    [categories, selected]
  );
  const categoryValue = categoryOptions.find((option) => option.value === formik.values.categoryId) ?? null;

  return (
    <Modal isShown={isShown} onHide={onHide}>
      <Text size="h2" align="center" className="mb-2">
        {isEdit ? t('Modal.editTitle', namespace) : t('Modal.createTitle', namespace)}
      </Text>
      <Text size="body-medium" color="Neutral700" align="center" className="mb-3">
        {t('Modal.subtitle', namespace)}
      </Text>

      <form onSubmit={formik.handleSubmit}>
        <TextField
          label={t('Modal.Fields.title.label', namespace)}
          placeholder={t('Modal.Fields.title.placeholder', namespace)}
          name="title"
          value={formik.values.title}
          onChange={formik.handleChange}
          error={{ message: formik.errors.title, touched: formik.touched.title }}
          className="mb-2"
          required
        />

        <SingleSelectField
          label={t('Modal.Fields.category.label', namespace)}
          placeholder={t('Modal.Fields.category.placeholder', namespace)}
          name="categoryId"
          value={categoryValue}
          onChange={(option) => formik.setFieldValue('categoryId', (option as { value: string } | null)?.value ?? '')}
          onBlur={() => formik.setFieldTouched('categoryId', true)}
          options={categoryOptions}
          error={{ message: formik.errors.categoryId, touched: formik.touched.categoryId }}
          className="mb-2"
          required
        />

        <TextAreaField
          label={t('Modal.Fields.description.label', namespace)}
          placeholder={t('Modal.Fields.description.placeholder', namespace)}
          name="description"
          value={formik.values.description}
          onChange={formik.handleChange}
          error={{ message: formik.errors.description, touched: formik.touched.description }}
          className="mb-2"
          required
        />

        <TextField
          label={t('Modal.Fields.location.label', namespace)}
          placeholder={t('Modal.Fields.location.placeholder', namespace)}
          name="location"
          value={formik.values.location}
          onChange={formik.handleChange}
          className="mb-2"
        />

        {!isEdit && (
          <>
            <FileField
              name="photos"
              label={t('Modal.Fields.photos.label', namespace)}
              filesLabel={`${namespace}.Modal.Fields.photos.filesLabel`}
              multiple={true}
              accept="image/*"
              reference={fileInputRef}
              formikValue={formik.values.photos}
              className="mt-2"
              onChange={(event) => {
                if (event && event.currentTarget && event.currentTarget.files) {
                  formik.setFieldValue('photos', event.currentTarget.files);
                }
              }}
            />
            <Text as="div" size="body-small" color="Neutral600" className="mb-3">
              {t('Modal.Fields.photos.hint', namespace)}
            </Text>
          </>
        )}

        <Button
          onClick={formik.handleSubmit}
          loading={formik.isSubmitting}
          disabledDeps={['demo', 'expired']}
          wide
          title={isEdit ? t('Modal.submitEdit', namespace) : t('Modal.submitCreate', namespace)}
        />
      </form>
    </Modal>
  );
};

export default IssueFormModal;
